import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageLayout, PageHeader, SectionCard, LoadingState, EmptyState } from '../../components/ui';
import { fetchAnalytics, fetchResponses, fetchResponse, downloadResultsCsv } from '../../services/surveysApi';

function StatTile({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 150, flex: '1 1 150px' }}>
      <Typography variant="h4">{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function SurveyResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([fetchAnalytics(id), fetchResponses(id, { per_page: 50 })]);
      setAnalytics(a);
      setResponses(r?.data || []);
    } catch {
      toast.error('Failed to load results.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openResponse = async (responseId) => {
    try {
      setDetail(await fetchResponse(id, responseId));
    } catch {
      toast.error('Could not load that response.');
    }
  };

  if (loading) return <LoadingState />;

  const totals = analytics?.analytics?.totals || {};
  const distribution = analytics?.analytics?.distribution || [];

  return (
    <PageLayout>
      <PageHeader
        title={`Results — ${analytics?.survey?.title || ''}`}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button onClick={() => navigate('/admin/surveys')}>Back</Button>
            <Button
              variant="contained"
              onClick={() => downloadResultsCsv(id).catch(() => toast.error('Export failed.'))}
            >
              Export CSV
            </Button>
          </Stack>
        )}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <StatTile label="Targeted" value={totals.targets ?? 0} />
        <StatTile label="Submitted" value={totals.submitted ?? 0} />
        <StatTile label="In progress" value={totals.in_progress ?? 0} />
        <StatTile label="Completion rate" value={totals.completion_rate != null ? `${totals.completion_rate}%` : '—'} />
      </Stack>

      {distribution.map((d) => (
        <SectionCard key={d.question_id} title={d.label} sx={{ mb: 2 }}>
          {Array.isArray(d.options) ? (
            <Stack spacing={1}>
              {(() => {
                const total = d.options.reduce((sum, o) => sum + (o.count || 0), 0) || 1;
                return d.options.map((o) => (
                  <Box key={o.option_id}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">{o.label || o.value}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {o.count} ({Math.round((o.count / total) * 100)}%)
                      </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={(o.count / total) * 100} sx={{ height: 8, borderRadius: 1 }} />
                  </Box>
                ));
              })()}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">{d.answers_count ?? 0} free-text answer(s)</Typography>
          )}
        </SectionCard>
      ))}

      <SectionCard title="Responses">
        {responses.length === 0 ? <EmptyState title="No responses yet" /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Respondent</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.respondent ? (r.respondent.name || r.respondent.employee_code) : 'Anonymous'}</TableCell>
                  <TableCell>{r.dept_snapshot || '—'}</TableCell>
                  <TableCell><Chip size="small" label={r.status} color={r.status === 'submitted' ? 'success' : 'warning'} /></TableCell>
                  <TableCell>{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openResponse(r.id)}>View path</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>Response #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          {(detail?.answers || []).map((a) => (
            <Box key={a.question_id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{a.question_label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {Array.isArray(a.value) ? a.value.join(', ') : String(a.value ?? '—')}
              </Typography>
            </Box>
          ))}
          {(detail?.answers || []).length === 0 && (
            <Typography variant="body2" color="text.secondary">No answers recorded.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
